import { ActionIcon, Badge, Container, Group, Menu, Stack, Table, Text, useMantineTheme } from "@mantine/core";
import type { TableIdentifier } from "@packages/lib";
import { IconDotsVertical, IconProgressHelp, IconSettings } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { TablerSvgToIcon } from "~/components/icons/tabler-svg-to-icon";
import { trpc } from "~/lib/trpc";
import { CustomizeFieldsModal } from "./-components/customize-fields-modal";
import { EditTableConfigModal } from "./-components/edit-table-config-modal";
import { getSchemaColor, type TableType, useTableConfigStore } from "./-components/lib";

export const Route = createFileRoute("/_authenticated/admin/tables/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc } }) => {
    const tables = await queryClient.ensureQueryData(trpc.crm.getTableList.queryOptions());
    return tables;
  },
});

function RouteComponent() {
  const tables = useQuery(trpc.crm.getTableList.queryOptions(undefined, { initialData: Route.useLoaderData() }));

  const sortedTables = useMemo(() => {
    return tables.data.toSorted((a, b) => {
      const nameA = a.nameEn || a.tableName;
      const nameB = b.nameEn || b.tableName;
      return nameA.localeCompare(nameB);
    });
  }, [tables.data]);

  return (
    <Container>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedTables.map((table) => (
            <Row key={table.schemaName + table.tableName} table={table} />
          ))}
        </Table.Tbody>
      </Table>
      <EditTableConfigModal />
      <CustomizeFieldsModal />
    </Container>
  );
}

function Row({ table }: { table: TableType }) {
  const theme = useMantineTheme();

  const displayName = table.nameEn || table.tableName;

  const icon = table.icon ? (
    <TablerSvgToIcon size={30} id={table.icon.id} svg={table.icon.svg} />
  ) : (
    <IconProgressHelp size={30} color={theme.colors.gray[4]} />
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Group>
          {icon}
          <Stack gap={0}>
            <Text>{displayName}</Text>
            <Group gap={2}>
              <Badge radius="sm" size="xs" color={getSchemaColor(table.schemaName)}>
                <Text c="dark.8" fz={10}>
                  {table.schemaName}
                </Text>
              </Badge>
              <Text size="xs" c="dimmed">
                {table.tableName}
              </Text>
            </Group>
          </Stack>
        </Group>
      </Table.Td>
      <Table.Td>{table.tableType === "VIEW" ? "View" : "Table"}</Table.Td>
      <Table.Td>
        <ActionMenu table={table} />
      </Table.Td>
    </Table.Tr>
  );
}

function ActionMenu(props: { table: TableType }) {
  const editTableConfig = useTableConfigStore((s) => s.setActiveTable);
  const tableId = `${props.table.schemaName}.${props.table.tableName}` as TableIdentifier;

  return (
    <Menu shadow="md" width={200} position="right">
      <Menu.Target>
        <ActionIcon variant="transparent">
          <IconDotsVertical color="var(--mantine-color-dark-4)" size={18} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconSettings size={14} />}
          onClick={() => editTableConfig(tableId, "customize-fields")}>
          Customise Fields
        </Menu.Item>
        <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => editTableConfig(tableId, "edit-config")}>
          Edit Configuration
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
